/**
 * Persisted chat threads — list/create/load/delete.
 *
 * Mirrors the BE `/api/v1/conversations` surface. The chat handler
 * only persists messages when a `conversation_id` is attached to the
 * stream request, so the FE creates the row explicitly via `create`
 * before the first send of a new thread.
 */
import { tokenStorage } from "@/auth/storage";

import { apiFetch, throwApiRequestError } from "./client";
import type {
  Conversation,
  ConversationDetail,
  CreateConversationRequest,
} from "./generated";

export function listConversations(): Promise<Conversation[]> {
  return apiFetch<Conversation[]>("/api/v1/conversations");
}

export function getConversation(id: string): Promise<ConversationDetail> {
  return apiFetch<ConversationDetail>(
    `/api/v1/conversations/${encodeURIComponent(id)}`,
  );
}

export function createConversation(
  req: CreateConversationRequest,
): Promise<Conversation> {
  return apiFetch<Conversation>("/api/v1/conversations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/** DELETE returns 204 No Content; bypass apiFetch which expects JSON. */
export async function deleteConversation(id: string): Promise<void> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const tokens = tokenStorage.load();
  if (tokens?.access_token) {
    headers.Authorization = `Bearer ${tokens.access_token}`;
  }
  const res = await fetch(`/api/v1/conversations/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers,
  });
  if (!res.ok) {
    await throwApiRequestError(res);
  }
}
